// Agent workspace detail — "what the agent actually did": its run(s) with the
// output summary, the artifact(s) it produced (rendered markdown), and any
// handoffs. Reads GET /api/agent-workspaces/:id (full detail).
//
// Phone renders in place (PHONE_IN_PLACE): the phone top bar already has Back
// and the page name, so the PageHeader goes; the name + meta sit in a header
// card, and each block uses the phone section header over a full-bleed card
// with 16px gutters and 14–15px text, like the record pages.

// Desktop: a bordered Section card. Phone: section header + full-bleed card.
const WsBlock = ({ phone, title, count, children }) => phone ? (
  <>
    <window.PhoneSectionHeader title={title} count={count} />
    <div style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>{children}</div>
  </>
) : (
  <Section title={count == null ? title : `${title} · ${count}`}>{children}</Section>
);

const WorkspaceDetail = ({ workspaceId, back }) => {
  const [ws, setWs] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const device = window.useDevice();
  const phone = !!device.phone;
  // Touch gets the .md-body class for its touch-scoped rules (wide tables
  // scroll, images fit; phone text size). Desktop leaves it off — the base
  // .md-body rules would retune its spacing.
  const mdClass = device.touch ? 'md-body' : undefined;

  React.useEffect(() => {
    let alive = true;
    setWs(null); setErr(null);
    window.AtlasApi.getAgentWorkspace(workspaceId)
      .then((d) => { if (alive) setWs(d); })
      .catch((e) => { if (alive) setErr(String(e && e.message || e)); });
    return () => { alive = false; };
  }, [workspaceId]);

  const md = (s) => {
    try { return { __html: window.DOMPurify.sanitize(window.marked.parse(s || '')) }; }
    catch (_) { return { __html: (s || '').replace(/</g, '&lt;') }; }
  };
  const statusHue = (s) => s === 'completed' ? 'green' : s === 'failed' ? 'rose' : s === 'running' ? 'blue' : 'slate';
  const meta = ws ? `${(ws.lead_agent_key || '').replace(/-/g, ' ')} · ${ws.focus_type} · ${ws.status}` : '';
  const rowPad = phone ? '12px 16px' : '12px 14px';
  const pillSz = phone ? 'md' : 'sm';

  const body = (
    <>
      {err && <div style={{ color: 'var(--danger, #c0392b)', fontSize: phone ? 15 : 13, ...(phone ? { padding: '14px 16px 0' } : null) }}>Couldn’t load workspace: {err}</div>}
      {!ws && !err && <div style={{ color: 'var(--text-3)', fontSize: phone ? 15 : 13, ...(phone ? { padding: '14px 16px 0' } : null) }}>Loading…</div>}
      {ws && (
        <>
          {ws.shared_context && (
            <WsBlock phone={phone} title="Context">
              <div style={{ padding: phone ? rowPad : 14, fontSize: phone ? 15 : 12.5, color: 'var(--text-2)', lineHeight: 1.55, whiteSpace: 'pre-wrap', ...(phone ? { overflowWrap: 'anywhere' } : null) }}>{ws.shared_context}</div>
            </WsBlock>
          )}

          <WsBlock phone={phone} title="Runs" count={(ws.runs || []).length}>
            <div>
              {(ws.runs || []).length === 0 && <div style={{ padding: phone ? rowPad : 14, fontSize: phone ? 14 : 12, color: 'var(--text-3)' }}>No runs yet.</div>}
              {(ws.runs || []).map((r, i) => (
                <div key={r.id} style={{ padding: rowPad, borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ width: phone ? 28 : 24, height: phone ? 28 : 24, borderRadius: 7, background: HUE.blue.bg, color: HUE.blue.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon.Bot size={phone ? 15 : 13} />
                    </span>
                    <span style={{ fontSize: phone ? 15 : 13, fontWeight: 600, color: 'var(--text)' }}>{(r.agent_key || '').replace(/-/g, ' ')}</span>
                    <Pill hue={statusHue(r.status)} dot sz={pillSz}>{r.status}</Pill>
                    {r.completed_at && <span style={{ fontSize: phone ? 13 : 11, color: 'var(--text-3)' }}>{relativeTime(r.completed_at)}</span>}
                  </div>
                  {r.goal && <div style={{ fontSize: phone ? 14 : 12, color: 'var(--text-3)', marginTop: 6, ...(phone ? { lineHeight: 1.45, overflowWrap: 'anywhere' } : null) }}><b style={{ color: 'var(--text-2)' }}>Goal:</b> {r.goal}</div>}
                  {/* Phone: no inline size — html.is-phone .md-body sets 14px. */}
                  {r.output_summary && <div className={mdClass} style={phone ? { color: 'var(--text)', marginTop: 8 } : { fontSize: 12.5, color: 'var(--text)', marginTop: 8, lineHeight: 1.6 }} dangerouslySetInnerHTML={md(r.output_summary)} />}
                </div>
              ))}
            </div>
          </WsBlock>

          <WsBlock phone={phone} title="Artifacts" count={(ws.artifacts || []).length}>
            <div>
              {(ws.artifacts || []).length === 0 && <div style={{ padding: phone ? rowPad : 14, fontSize: phone ? 14 : 12, color: 'var(--text-3)' }}>No artifacts produced.</div>}
              {(ws.artifacts || []).map((a, i) => (
                <div key={a.id} style={{ padding: rowPad, borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Pill hue="violet" sz={pillSz}>{a.kind}</Pill>
                    <span style={{ fontSize: phone ? 15 : 13, fontWeight: 600, color: 'var(--text)', ...(phone ? { overflowWrap: 'anywhere' } : null) }}>{a.title}</span>
                    {a.created_by && <span style={{ fontSize: phone ? 13 : 11, color: 'var(--text-3)' }}>· {a.created_by}</span>}
                  </div>
                  {a.body && <div className={mdClass} style={phone ? { color: 'var(--text-2)', marginTop: 8 } : { fontSize: 12.5, color: 'var(--text-2)', marginTop: 8, lineHeight: 1.6, overflowWrap: 'anywhere' }} dangerouslySetInnerHTML={md(a.body)} />}
                </div>
              ))}
            </div>
          </WsBlock>

          {ws.handoffs && ws.handoffs.length > 0 && (
            <WsBlock phone={phone} title="Handoffs" count={ws.handoffs.length}>
              <div>
                {ws.handoffs.map((h, i) => (
                  <div key={h.id} style={{ padding: phone ? rowPad : '10px 14px', borderTop: i === 0 ? 'none' : '1px solid var(--border)', fontSize: phone ? 14 : 12, color: 'var(--text-2)', ...(phone ? { lineHeight: 1.45, overflowWrap: 'anywhere' } : null) }}>
                    <span className="mono">{h.from_agent_key} → {h.to_agent_key}</span>{h.note ? ` · ${h.note}` : ''}
                  </div>
                ))}
              </div>
            </WsBlock>
          )}
        </>
      )}
    </>
  );

  if (phone) {
    return (
      <window.PhoneScroll style={{ background: 'var(--bg)' }}>
        {ws && (
          <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '14px 16px 16px' }}>
            <h1 style={{ fontSize: 20, fontWeight: 650, lineHeight: 1.25, color: 'var(--text)', letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>{ws.name}</h1>
            {meta && <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.4 }}>{meta}</div>}
          </div>
        )}
        {body}
      </window.PhoneScroll>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Agents', 'Run']}
        title={ws ? ws.name : 'Agent workspace'}
        meta={meta}
        actions={back ? <Btn size="sm" variant="outline" icon={<Icon.ChevronL size={12} />} onClick={back}>Back</Btn> : null}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {body}
      </div>
    </div>
  );
};

window.WorkspaceDetail = WorkspaceDetail;
